import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PageHeader from '../../components/PageHeader/PageHeader';
import LearningService from '../../services/LearningService';
import { storedLearningAccess } from '../../utils/learningAccess';

const learningFont = '"Roboto", sans-serif';

const fallbackGuides = [
  {
    id: 'company-setup',
    title: 'Configurá tu empresa',
    description: 'Completá los datos principales de tu empresa para empezar a operar en Axium.',
    category: 'Primeros pasos',
    level: 'Básico',
    duration: '5 min',
    isReady: false,
    steps: ['Ingresá a Configuraciones.', 'Revisá los datos de la empresa y guardá los cambios.', 'Confirmá que las sucursales estén correctamente creadas.'],
  },
  {
    id: 'first-product',
    title: 'Creá tu primer producto',
    description: 'Conocé los tipos de producto y elegí la guía correcta antes de empezar el alta.',
    category: 'Productos',
    level: 'Básico',
    duration: '4 min',
    isReady: true,
    steps: [
      {
        title: 'Entrá al catálogo de productos',
        description: 'Desde el menú elegí la sucursal en la que vas a administrar el producto y abrí Productos.',
        assetKey: 'catalog',
        imageAlt: 'Catálogo de productos de la empresa E2E',
        focus: { top: '35%', left: '89%' },
        callout: 'Usá “Agregar producto” para iniciar el alta.',
      },
      {
        title: 'Elegí el tipo de producto',
        description: 'Antes de avanzar, definí cómo se va a controlar el producto. El tipo determina las etapas que vas a completar y cómo se descuenta el stock al vender.',
        assetKey: 'productType',
        imageAlt: 'Selector de tipo de producto en el entorno E2E',
        focus: { top: '59%', left: '35%' },
        callout: 'Para un producto elaborado que siempre se vende igual, elegí “Definido”.',
        details: [
          {
            title: 'Definido',
            description: 'Producto ya preparado o con composición conocida. Al venderlo, Axium descuenta el stock del producto.',
          },
          {
            title: 'Indefinido',
            description: 'El contenido se determina al vender. Axium solicita los ítems correspondientes y descuenta el stock de los elegidos.',
          },
          {
            title: 'Retail',
            description: 'Producto comprado para reventa, sin packaging, contenido ni toppings. Al venderlo, Axium descuenta el stock del producto.',
          },
        ],
      },
      {
        title: 'Completá los datos principales',
        description: 'Cada tipo tiene un recorrido propio para mostrarte únicamente los campos que necesitás completar.',
        assetKey: 'productType',
        imageAlt: 'Selector de tipo de producto en el entorno E2E',
        focus: { top: '59%', left: '35%' },
        callout: 'Elegí una de las guías específicas para continuar con el alta.',
        relatedGuideIds: ['defined-product', 'undefined-product', 'retail-product'],
      },
    ],
  },
  {
    id: 'defined-product',
    title: 'Creá un producto definido',
    description: 'Para productos elaborados o con una composición conocida antes de la venta.',
    category: 'Productos',
    level: 'Básico',
    duration: '8 min',
    isReady: true,
    steps: [
      {
        title: 'Completá los datos principales',
        description: 'Ingresá nombre y precio de venta. Elegí categoría y subcategoría para clasificar el producto; el código de barras es opcional.',
        assetKey: 'basicData',
        imageAlt: 'Campos obligatorios de un producto definido en el entorno E2E',
        focus: { top: '49%', left: '51%' },
        callout: 'Nombre, precio, categoría y subcategoría son obligatorios.',
      },
      {
        title: 'Definí el packaging',
        description: 'Agregá los envases, bolsas o empaques que se usan al entregar el producto. Esto ayuda a reflejar su composición y costo.',
        assetKey: 'packaging',
        imageAlt: 'Etapa de packaging en el alta de producto E2E',
        focus: { top: '28%', left: '39%' },
        callout: 'Si el producto no usa packaging, podés continuar sin agregar ítems.',
      },
      {
        title: 'Cargá el contenido',
        description: 'Agregá los insumos, preparaciones o productos que componen el producto. Indicá la cantidad y la unidad de cada ítem.',
        assetKey: 'content',
        imageAlt: 'Etapa de contenido en el alta de producto E2E',
        focus: { top: '28%', left: '49%' },
        callout: 'Usá esta etapa para documentar qué incluye el producto definido.',
      },
      {
        title: 'Sumá toppings si corresponde',
        description: 'Cargá los agregados que se incorporan al producto, como salsas, extras o terminaciones. Si no aplica, seguí al próximo paso.',
        assetKey: 'toppings',
        imageAlt: 'Etapa de toppings en el alta de producto E2E',
        focus: { top: '28%', left: '63%' },
        callout: 'Los toppings sirven para dejar registrada la composición completa del producto.',
      },
      {
        title: 'Agregá una imagen y guardá',
        description: 'La imagen es opcional, pero facilita que el equipo identifique el producto. Cuando terminaste de revisar la información, hacé clic en Guardar.',
        assetKey: 'image',
        imageAlt: 'Etapa de imagen y guardado en el alta de producto E2E',
        focus: { top: '78%', left: '79%' },
        callout: 'Al guardar, el producto queda disponible en la sucursal seleccionada.',
      },
    ],
  },
  {
    id: 'undefined-product',
    title: 'Creá un producto indefinido',
    description: 'Para productos cuyo contenido se termina de definir al momento de la venta.',
    category: 'Productos',
    level: 'Intermedio',
    duration: '9 min',
    isReady: true,
    steps: [
      {
        title: 'Elegí “Indefinido” y completá la información básica',
        description: 'Ingresá nombre, precio, categoría y subcategoría. Elegí el tipo Indefinido para habilitar sus etapas específicas.',
        assetKey: 'type',
        imageAlt: 'Tipo de producto indefinido en el entorno E2E',
        focus: { top: '59%', left: '35%' },
        callout: 'Categoría y subcategoría son obligatorias: se usan al resolver el contenido durante la venta.',
      },
      {
        title: 'Cargá el contenido definido',
        description: 'Agregá los ítems que siempre forman parte del producto, con su cantidad y unidad.',
        assetKey: 'definedContent',
        imageAlt: 'Etapa de contenido definido para un producto indefinido E2E',
        focus: { top: '28%', left: '49%' },
        callout: 'Este contenido queda establecido desde el alta.',
      },
      {
        title: 'Definí el contenido a resolver en la venta',
        description: 'Indicá qué categorías y subcategorías se deben elegir cuando se vende el producto.',
        assetKey: 'undefinedContent',
        imageAlt: 'Etapa de contenido indefinido en el entorno E2E',
        focus: { top: '28%', left: '49%' },
        callout: 'Al facturar, Axium pide seleccionar los ítems reales y descuenta el stock elegido.',
      },
      {
        title: 'Configurá los opcionales',
        description: 'Agregá productos concretos que el cliente puede sumar y definí cuántos puede elegir.',
        assetKey: 'optionals',
        imageAlt: 'Etapa de opcionales en el entorno E2E',
        focus: { top: '33%', left: '48%' },
        callout: 'Los opcionales pueden tener un costo adicional.',
      },
      {
        title: 'Agregá una imagen y guardá',
        description: 'La imagen es opcional. Revisá la información y guardá para dejar el producto disponible.',
        assetKey: 'image',
        imageAlt: 'Etapa de imagen para un producto indefinido E2E',
        focus: { top: '78%', left: '79%' },
        callout: 'El producto queda listo para resolver su contenido durante la venta.',
      },
    ],
  },
  {
    id: 'retail-product',
    title: 'Creá un producto retail',
    description: 'Para productos comprados a un proveedor y vendidos directamente, como bebidas envasadas.',
    category: 'Productos',
    level: 'Básico',
    duration: '5 min',
    isReady: true,
    steps: [
      {
        title: 'Elegí “Retail” y completá la información básica',
        description: 'Ingresá nombre, precio, categoría y subcategoría. Después elegí Retail como tipo de producto.',
        assetKey: 'basicData',
        imageAlt: 'Información básica de un producto retail en el entorno E2E',
        focus: { top: '59%', left: '35%' },
        callout: 'Los productos retail no tienen etapas de packaging, contenido ni toppings.',
      },
      {
        title: 'Indicá el proveedor',
        description: 'Seleccioná el proveedor principal; es obligatorio para productos retail. El proveedor secundario es opcional.',
        assetKey: 'supplier',
        imageAlt: 'Campos de proveedor para producto retail en el entorno E2E',
        focus: { top: '74%', left: '36%' },
        callout: 'Al venderlo, Axium descuenta directamente el stock del producto retail.',
      },
      {
        title: 'Agregá una imagen y guardá',
        description: 'La imagen es opcional. Revisá los datos y guardá para dejarlo disponible en la sucursal.',
        assetKey: 'image',
        imageAlt: 'Etapa de imagen y guardado de producto retail',
        focus: { top: '78%', left: '79%' },
        callout: 'Usá una imagen para que el equipo lo encuentre más rápido en la operación.',
      },
    ],
  },
  {
    id: 'first-sale',
    title: 'Registrá una venta',
    description: 'Conocé el flujo para abrir caja, cargar una venta y registrar el cobro.',
    category: 'Ventas y caja',
    level: 'Básico',
    duration: '6 min',
    isReady: false,
    steps: ['Abrí la caja de la sucursal correspondiente.', 'Seleccioná los productos y asociá un cliente si corresponde.', 'Elegí el medio de pago y confirmá la operación.'],
  },
  {
    id: 'stock-control',
    title: 'Consultá y ajustá el stock',
    description: 'Revisá las existencias actuales y registrá movimientos para mantenerlas actualizadas.',
    category: 'Stock',
    level: 'Intermedio',
    duration: '8 min',
    isReady: false,
    steps: ['Abrí el módulo de Stock.', 'Filtrá por sucursal o producto para encontrar la existencia.', 'Registrá el movimiento y verificá el saldo actualizado.'],
  },
  {
    id: 'invite-team',
    title: 'Invitá a tu equipo',
    description: 'Creá usuarios y asignales el rol adecuado según las tareas que realizan.',
    category: 'Usuarios y roles',
    level: 'Básico',
    duration: '5 min',
    isReady: false,
    steps: ['Ingresá a Usuarios.', 'Agregá los datos de la persona y asignale un rol.', 'Verificá sus permisos antes de enviarle el acceso.'],
  },
  {
    id: 'sales-report',
    title: 'Leé el reporte de ventas',
    description: 'Usá los filtros y métricas clave para conocer el desempeño de tus sucursales.',
    category: 'Reportes',
    level: 'Intermedio',
    duration: '6 min',
    isReady: false,
    steps: ['Abrí Reportes de ventas.', 'Elegí el período y las sucursales a analizar.', 'Revisá ventas, ticket promedio y medios de pago.'],
  },
];

const normalize = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const learningCategoryOrder = [
  'Primeros pasos',
  'Administración',
  'Clientes',
  'Proveedores',
  'Productos',
  'Insumos',
  'Preparaciones y producción',
  'Inventario y sucursales',
  'Ventas y caja',
  'Facturación y contabilidad',
  'Gastos',
  'Reportes',
  'E-commerce',
  'B2B',
  'Marketing y descuentos',
];

export const learningCategoryOptions = (guides) => {
  const categoryRank = new Map(learningCategoryOrder.map((category, index) => [category, index]));
  const categories = [...new Set(guides.map((guide) => guide.category))]
    .sort((first, second) => {
      const rankDifference = (categoryRank.get(first) ?? Infinity) - (categoryRank.get(second) ?? Infinity);
      return rankDifference || first.localeCompare(second, 'es');
    });

  return [
    { value: 'Todos', label: 'Todas las categorías', count: guides.length },
    ...categories.map((item) => ({
      value: item,
      label: item,
      count: guides.filter((guide) => guide.category === item).length,
    })),
  ];
};

export const filterLearningGuides = (items, search, category) => {
  const normalizedSearch = normalize(search).trim();

  return items.filter((guide) => {
    const matchesCategory = category === 'Todos' || guide.category === category;
    const searchable = normalize(`${guide.title} ${guide.description} ${guide.category} ${guide.level}`);
    return matchesCategory && (!normalizedSearch || searchable.includes(normalizedSearch));
  });
};

export const calloutToLines = (callout) => String(callout || '')
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean)
  .map((line) => {
    const separatorIndex = line.indexOf('|');
    if (separatorIndex === -1) return { label: '', description: line };
    return {
      label: line.slice(0, separatorIndex).trim(),
      description: line.slice(separatorIndex + 1).trim(),
    };
  });

export const courseToGuide = (course) => ({
  ...course,
  isReady: true,
  steps: (course.sections || []).map((section) => ({
    title: section.title,
    description: section.description,
    callout: section.callout,
    imageUrl: section.imageUrl,
    imageAlt: section.imageAlt || section.title,
    focus: section.focusTop && section.focusLeft
      ? { top: section.focusTop, left: section.focusLeft }
      : undefined,
    imageMarkers: section.imageMarkers || [],
    details: section.details || [],
    relatedGuideIds: section.relatedCourseIds || [],
  })),
});

const levelBadgeSx = (level) => ({
  Básico: { bgcolor: '#e9f8f0', color: '#147a4d' },
  Intermedio: { bgcolor: '#fff4dc', color: '#9a6100' },
  Avanzado: { bgcolor: '#fdeaea', color: '#b42318' },
}[level] || { bgcolor: '#f4f7fb', color: 'text.primary' });

const asGuideSteps = (guide) => guide.steps.map((step, index) => (
  typeof step === 'string'
    ? { title: `Paso ${index + 1}`, description: step }
    : step
));

const GuideReader = ({ guide, guideList, onBack, onOpenGuide }) => {
  const steps = asGuideSteps(guide);
  const [currentStep, setCurrentStep] = useState(0);
  const [assetUrls, setAssetUrls] = useState({});
  const [assetsError, setAssetsError] = useState('');
  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const hasStaticAssets = steps.some((item) => item.assetKey && !item.imageUrl);
  const imageUrl = step.imageUrl || (step.assetKey ? assetUrls[step.assetKey] : null);
  const hasImage = Boolean(step.imageUrl || step.assetKey);
  const imageMarkers = step.imageMarkers?.length ? step.imageMarkers : (step.focus ? [{ number: currentStep + 1, reference: '', top: step.focus.top, left: step.focus.left }] : []);

  useEffect(() => {
    setCurrentStep(0);
  }, [guide.id]);

  useEffect(() => {
    if (!hasStaticAssets) return undefined;

    let active = true;
    setAssetUrls({});
    setAssetsError('');
    LearningService.getGuideAssets(guide.id)
      .then((response) => {
        if (active) setAssetUrls(response?.assets || {});
      })
      .catch(() => {
        if (active) setAssetsError('No se pudieron cargar las imágenes de esta guía. Volvé a intentarlo en unos instantes.');
      });

    return () => { active = false; };
  }, [guide.id, hasStaticAssets]);

  const move = (direction) => {
    setCurrentStep((value) => Math.max(0, Math.min(steps.length - 1, value + direction)));
  };

  return (
    <Box p={3} sx={{ fontFamily: learningFont, '& .MuiTypography-root, & .MuiChip-label': { fontFamily: learningFont }, '& .MuiButton-root': { fontFamily: learningFont, fontWeight: 500, textTransform: 'none' } }}>
      <Button startIcon={<ArrowBackOutlinedIcon />} onClick={onBack} sx={{ mb: 2 }}>
        Volver a las guías
      </Button>

      <Paper elevation={0} sx={{ p: { xs: 2.25, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ md: 'flex-start' }}>
          <Box>
            <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ mb: 1.25 }}>
              <Chip size="small" label={guide.category} color="primary" variant="outlined" />
              <Chip size="small" label={guide.level} sx={{ ...levelBadgeSx(guide.level), fontWeight: 500 }} />
              <Chip size="small" icon={<AccessTimeOutlinedIcon />} label={guide.duration} variant="outlined" />
            </Stack>
            <Typography variant="h4" fontWeight={500}>{guide.title}</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>{guide.description}</Typography>
          </Box>
          <Box sx={{ minWidth: { md: 180 } }}>
            <Typography variant="caption" color="text.secondary">Progreso de la guía</Typography>
            <Typography fontWeight={500} sx={{ mt: 0.25 }}>Paso {currentStep + 1} de {steps.length}</Typography>
            <LinearProgress variant="determinate" value={((currentStep + 1) / steps.length) * 100} sx={{ mt: 1, height: 7, borderRadius: 5 }} />
          </Box>
        </Stack>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 1 }}>
            {steps.map((item, index) => {
              const active = currentStep === index;
              const completed = currentStep > index;
              return (
                <Button
                  key={item.title}
                  fullWidth
                  onClick={() => setCurrentStep(index)}
                  startIcon={completed ? <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 24 }} /> : (
                    <Box
                      component="span"
                      sx={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 9,
                        lineHeight: 1,
                        fontWeight: 500,
                        border: '1px solid',
                        borderColor: active ? 'primary.main' : 'divider'
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}
                  sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    textAlign: 'left',
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontSize: '0.9rem',
                    lineHeight: 1.35,
                    px: 1.25,
                    py: 1.1,
                    mb: 0.25,
                    color: active ? 'primary.main' : 'text.primary',
                    bgcolor: active ? '#eef5ff' : 'transparent',
                    fontWeight: 400,
                    '& .MuiButton-startIcon': { my: 0, alignSelf: 'center' },
                    '&:hover': { bgcolor: active ? '#eef5ff' : '#f7f9fc' },
                  }}
                >
                  {item.title}
                </Button>
              );
            })}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, md: 3 }, pb: hasImage ? 2 : 3 }}>
              <Typography variant="overline" color="primary.main" fontWeight={500}>Paso {currentStep + 1}</Typography>
              <Typography variant="h5" fontWeight={500} sx={{ mt: 0.25 }}>{step.title}</Typography>
              <Typography color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-line' }}>{step.description}</Typography>
            </Box>

            {hasImage && (
              <Box sx={{ px: { xs: 2, md: 3 }, pb: 2.5 }}>
                {assetsError && <Alert severity="warning">{assetsError}</Alert>}
                {!assetsError && !imageUrl && (
                  <Box sx={{ minHeight: 280, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', display: 'grid', placeItems: 'center', bgcolor: '#f6f8fb' }}>
                    <CircularProgress size={28} />
                  </Box>
                )}
                {imageUrl && <>
                  <Box sx={{ position: 'relative', borderRadius: 2.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: '#f6f8fb' }}>
                    <Box component="img" src={imageUrl} alt={step.imageAlt} sx={{ width: '100%', display: 'block' }} />
                    {imageMarkers.map((marker) => <Box key={`${marker.number}-${marker.reference}`} sx={{ position: 'absolute', top: marker.top, left: marker.left, transform: 'translate(-50%, -50%)', width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: '#fff', fontWeight: 500, border: '3px solid #fff', boxShadow: 3 }}>
                      {marker.number}
                    </Box>)}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.25, p: 1.25, borderRadius: 2, bgcolor: '#eef5ff', color: '#174e91' }}>
                    <TipsAndUpdatesOutlinedIcon fontSize="small" sx={{ mt: 0.1 }} />
                    <Box>
                      {calloutToLines(step.callout).map((line, index) => (
                        <Typography key={`${line.label}-${line.description}-${index}`} variant="body2" fontWeight={line.label ? 400 : 500} sx={{ mt: index ? 0.75 : 0 }}>
                          {line.label && <Box component="span" sx={{ fontWeight: 500 }}>{line.label}</Box>}{line.label && line.description ? ' ' : ''}{line.description}
                        </Typography>
                      ))}
                      {step.details?.map((detail) => (
                        <Typography key={detail.title} variant="body2" sx={{ mt: 0.75, color: 'inherit' }}>
                          <Box component="span" sx={{ fontWeight: 500 }}>{detail.title}:</Box> {detail.description}
                        </Typography>
                      ))}
                      {imageMarkers.filter((marker) => marker.reference).map((marker) => (
                        <Typography key={`${marker.number}-${marker.reference}`} variant="body2" sx={{ mt: 0.75, color: 'inherit' }}>
                          <Box component="span" sx={{ fontWeight: 500 }}>{marker.number}.</Box> {marker.reference}
                        </Typography>
                      ))}
                    </Box>
                  </Stack>
                  {step.relatedGuideIds?.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>Continuá con una guía específica:</Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {step.relatedGuideIds.map((guideId) => {
                          const relatedGuide = guideList.find((item) => item.id === guideId);
                          if (!relatedGuide) return null;
                          return (
                            <Button key={guideId} size="small" variant="outlined" onClick={() => onOpenGuide(relatedGuide)}>
                              {relatedGuide.title}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </>}
              </Box>
            )}

            <Divider />
            <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ p: 2 }}>
              <Button startIcon={<ChevronLeftOutlinedIcon />} onClick={() => move(-1)} disabled={currentStep === 0}>
                Anterior
              </Button>
              <Button variant="contained" endIcon={isLastStep ? <CheckCircleOutlineOutlinedIcon /> : <ChevronRightOutlinedIcon />} onClick={() => isLastStep ? onBack() : move(1)}>
                {isLastStep ? 'Finalizar guía' : 'Siguiente'}
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const LearningPage = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Todos');
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [guides, setGuides] = useState(fallbackGuides);
  const canViewLearning = storedLearningAccess();
  const categoryOptions = useMemo(() => learningCategoryOptions(guides), [guides]);
  const visibleGuides = useMemo(() => filterLearningGuides(guides, search, category), [guides, search, category]);

  useEffect(() => {
    if (!canViewLearning) return undefined;

    let active = true;
    LearningService.getCourses()
      .then((courses) => {
        if (active && Array.isArray(courses) && courses.length > 0) {
          setGuides(courses.map(courseToGuide));
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [canViewLearning]);

  if (!canViewLearning) {
    return <Box p={3}><Alert severity="warning">No tenés permiso para ver Aprendizaje.</Alert></Box>;
  }

  if (selectedGuide) {
    return <GuideReader guide={selectedGuide} guideList={guides} onBack={() => setSelectedGuide(null)} onOpenGuide={setSelectedGuide} />;
  }

  return (
    <Box p={3} sx={{ fontFamily: learningFont, '& .MuiTypography-root, & .MuiChip-label, & .MuiInputBase-root': { fontFamily: learningFont }, '& .MuiButton-root': { fontFamily: learningFont, fontWeight: 500, textTransform: 'none' } }}>
      <>
        <PageHeader
          title="Centro de aprendizaje"
          subtitle="Guías breves para que tu equipo aprenda a usar Axium en cada proceso."
        />
      </>

      <>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.25, md: 3 },
          mb: 3,
          borderRadius: 3,
          color: '#fff',
          background: 'linear-gradient(120deg, #1266d8 0%, #348ee8 100%)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', width: 240, height: 240, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.09)', right: -80, top: -125 }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ position: 'relative' }}>
          <Box sx={{ width: 52, height: 52, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.16)' }}>
            <SchoolOutlinedIcon fontSize="large" />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={500}>Aprendé a tu ritmo</Typography>
            <Typography sx={{ mt: 0.5, opacity: 0.9 }}>Encontrá una guía rápida para cada tarea importante de tu operación.</Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: { xs: 2, md: 2.5 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscá una guía, proceso o módulo"
            inputProps={{ 'aria-label': 'Buscar en el Centro de aprendizaje' }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchOutlinedIcon color="action" /></InputAdornment>,
            }}
          />
          <TextField
            select
            label="Categoría"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            SelectProps={{
              inputProps: { 'aria-label': 'Categoría' },
              renderValue: (value) => {
                const option = categoryOptions.find((item) => item.value === value);
                return option ? `${option.label} (${option.count})` : value;
              },
            }}
            sx={{ minWidth: { md: 310 } }}
            helperText="Elegí una categoría para acotar las guías"
          >
            {categoryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label} ({option.count})
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={500}>Guías disponibles</Typography>
        <Typography variant="body2" color="text.secondary">{visibleGuides.length} {visibleGuides.length === 1 ? 'guía encontrada' : 'guías encontradas'}</Typography>
      </Stack>

      <Grid container columnSpacing={2.25} rowSpacing={2.25}>
        {visibleGuides.map((guide) => (
          <Grid item xs={12} sm={6} lg={4} key={guide.id} sx={{ display: 'flex' }}>
            <Paper
              component="article"
              elevation={0}
              sx={{
                width: '100%',
                alignSelf: 'flex-start',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                p: 1.75,
                transition: 'box-shadow .2s ease, transform .2s ease',
                '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Chip size="small" label={guide.category} color="primary" variant="outlined" />
                <Stack direction="row" alignItems="center" spacing={0.5} color="text.secondary">
                  <AccessTimeOutlinedIcon fontSize="small" />
                  <Typography variant="caption">{guide.duration}</Typography>
                </Stack>
              </Stack>
              <Typography variant="h6" fontWeight={500} sx={{ mt: 1.25, lineHeight: 1.25 }}>{guide.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65 }}>{guide.description}</Typography>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
                <Chip size="small" label={guide.level} sx={{ ...levelBadgeSx(guide.level), fontWeight: 500 }} />
                {guide.isReady ? (
                  <Button
                    size="small"
                    aria-label={`Ver guía: ${guide.title}`}
                    endIcon={<ArrowForwardOutlinedIcon />}
                    onClick={() => setSelectedGuide(guide)}
                  >
                    Ver guía
                  </Button>
                ) : (
                  <Chip size="small" label="Próximamente" variant="outlined" sx={{ color: 'text.secondary', borderColor: 'divider' }} />
                )}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {visibleGuides.length === 0 && (
        <Paper elevation={0} sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 3, py: 6, px: 2, textAlign: 'center' }}>
          <MenuBookOutlinedIcon color="action" sx={{ fontSize: 36 }} />
          <Typography fontWeight={500} sx={{ mt: 1 }}>No encontramos una guía con esa búsqueda</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Probá con otra palabra o quitá el filtro seleccionado.</Typography>
          <Button sx={{ mt: 1.5 }} onClick={() => { setSearch(''); setCategory('Todos'); }}>Limpiar filtros</Button>
        </Paper>
      )}
      </>

    </Box>
  );
};

export default LearningPage;
